<script setup lang="ts">
import {computed, reactive, ref} from "vue";
import type {CharacterOnPosition, GameMasterCharacterInfo} from "@/api/dx-backend";

type SortKey = "name-asc" | "name-desc" | "rank-desc" | "health-asc" | "status";
type StatusFilter = "all" | "active" | "down";

interface Props {
  characters: CharacterOnPosition[];
  vitals?: Record<string, GameMasterCharacterInfo>;
  selectedId?: string | null;
  title?: string;
}

const props = withDefaults(defineProps<Props>(), {
  vitals: () => ({}),
  selectedId: null,
  title: "No character selected",
});

const emit = defineEmits<{ select: [characterId: string] }>();

const searchQuery = ref("");
/* Portraits whose file no longer exists on disk fall back to initials rather
   than leaving an unidentifiable empty tile. */
const brokenAvatars = reactive(new Set<string>());
const sortKey = ref<SortKey>("name-asc");
const statusFilter = ref<StatusFilter>("all");
/* NPCs outnumber players by an order of magnitude, so the roster opens on the
   people a GM actually switches between. */
const showNpc = ref(false);

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  {value: "name-asc", label: "Name A → Z"},
  {value: "name-desc", label: "Name Z → A"},
  {value: "rank-desc", label: "Rank, highest first"},
  {value: "health-asc", label: "Health, lowest first"},
  {value: "status", label: "Status, standing first"},
];

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  {value: "all", label: "All"},
  {value: "active", label: "Standing"},
  {value: "down", label: "Down"},
];

const infoFor = (character: CharacterOnPosition) => props.vitals?.[character.id];

/* The roster payload carries a full biography the generated type does not
   declare, and it is the only place the portrait and rank actually arrive. */
const detailOf = (character: CharacterOnPosition) => character as CharacterOnPosition & {
  biography?: { avatar?: string | null };
  rank?: { name?: string; grade?: number };
};

const avatarFor = (character: CharacterOnPosition) =>
    detailOf(character).biography?.avatar || character.avatar || "";

const rankFor = (character: CharacterOnPosition) =>
    detailOf(character).rank || infoFor(character)?.rank;

const initialsFor = (character: CharacterOnPosition) =>
    (character.name || "?")
        .split(/\s+/)
        .slice(0, 2)
        .map((word) => word.charAt(0).toUpperCase())
        .join("");

const attribute = (character: CharacterOnPosition, name: string) =>
    infoFor(character)?.attributes?.find((attr) => attr.name === name);

const healthRatio = (character: CharacterOnPosition) => {
  const health = attribute(character, "Health");
  if (!health || !health.max) return null;
  return Math.max(0, Math.min(1, (health.current ?? 0) / health.max));
};

const healthLabel = (character: CharacterOnPosition) => {
  const health = attribute(character, "Health");
  if (!health || !health.max) return "—";
  return `${health.current ?? 0} / ${health.max}`;
};

const isDown = (character: CharacterOnPosition) =>
    character.alive === false || (healthRatio(character) ?? 1) <= 0;

const visibleCharacters = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  const filtered = props.characters.filter((character) => {
    if (!showNpc.value && character.npc) return false;
    if (statusFilter.value === "active" && isDown(character)) return false;
    if (statusFilter.value === "down" && !isDown(character)) return false;
    if (!query) return true;
    return (
        character.name?.toLowerCase().includes(query) ||
        character.id.toLowerCase().includes(query) ||
        rankFor(character)?.name?.toLowerCase().includes(query)
    );
  });

  const byName = (a: CharacterOnPosition, b: CharacterOnPosition) =>
      (a.name || "").localeCompare(b.name || "");

  return [...filtered].sort((a, b) => {
    switch (sortKey.value) {
      case "name-desc":
        return byName(b, a);
      case "rank-desc":
        return (rankFor(b)?.grade ?? -1) - (rankFor(a)?.grade ?? -1) || byName(a, b);
      case "health-asc":
        return (healthRatio(a) ?? 2) - (healthRatio(b) ?? 2) || byName(a, b);
      case "status":
        return Number(isDown(a)) - Number(isDown(b)) || byName(a, b);
      default:
        return byName(a, b);
    }
  });
});
</script>

<template>
  <div class="picker">
    <div class="picker__backdrop" aria-hidden="true"></div>

    <div class="picker__stack">
    <header class="picker__head">
      <h2 class="picker__title">{{ title }}</h2>

      <div class="picker__controls">
        <input
            v-model="searchQuery"
            type="search"
            class="gm-input picker__search"
            placeholder="Search by name, rank or UUID…"
        />
        <select v-model="sortKey" class="gm-select picker__sort">
          <option v-for="option in SORT_OPTIONS" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
        <div class="picker__segments">
          <button
              v-for="option in STATUS_OPTIONS"
              :key="option.value"
              type="button"
              class="gm-btn gm-btn--sm gm-btn--ghost"
              :class="{ 'is-active': statusFilter === option.value }"
              @click="statusFilter = option.value"
          >
            {{ option.label }}
          </button>
          <button
              type="button"
              class="gm-btn gm-btn--sm gm-btn--ghost"
              :class="{ 'is-active': showNpc }"
              title="Include NPCs in the roster"
              @click="showNpc = !showNpc"
          >
            NPCs
          </button>
        </div>
        <span class="picker__count">{{ visibleCharacters.length }} / {{ characters.length }}</span>
      </div>
    </header>

    <div class="picker__body gm-scroll">
      <div v-if="visibleCharacters.length" class="picker__grid-list">
        <button
            v-for="character in visibleCharacters"
            :key="character.id"
            type="button"
            class="pc"
            :class="{ 'pc--selected': character.id === selectedId, 'pc--down': isDown(character) }"
            :title="`${character.name}${rankFor(character) ? ' — ' + rankFor(character)?.name : ''} · ${healthLabel(character)}`"
            @click="emit('select', character.id)"
        >
          <span class="pc__avatar">
            <img
                v-if="avatarFor(character) && !brokenAvatars.has(character.id)"
                class="pc__portrait"
                :src="avatarFor(character)"
                :alt="character.name"
                loading="lazy"
                @error="brokenAvatars.add(character.id)"
            />
            <span v-else class="pc__initials">{{ initialsFor(character) }}</span>
          </span>
          <span v-if="character.npc" class="pc__dot pc__dot--npc" aria-hidden="true"></span>
          <span v-if="isDown(character)" class="pc__dot pc__dot--down" aria-hidden="true"></span>
          <span class="pc__caption">{{ character.name }}</span>
        </button>
      </div>

      <div v-else class="gm-empty picker__none">
        <span class="gm-empty__title">Nobody matches</span>
        <span class="gm-empty__hint">Loosen the search or the status filter.</span>
      </div>
    </div>
    </div>
  </div>
</template>

<style scoped>
.picker {
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap-sm);
  padding: var(--gm-gap);
  border-radius: var(--gm-radius);
  background: var(--gm-surface-sunken);
  box-shadow: inset 0 0 0 1px var(--gm-border-dim);
  overflow: hidden;
  isolation: isolate;
}

/* The plate is graded down so a roster of portraits stays the brightest thing
   in the panel. */
.picker__backdrop {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background-image:
      radial-gradient(90% 70% at 50% 90%, rgba(12, 14, 15, 0.55) 0%, rgba(12, 14, 15, 0.92) 100%),
      linear-gradient(rgba(12, 14, 15, 0.75), rgba(12, 14, 15, 0.55) 45%, rgba(12, 14, 15, 0.9) 100%),
      url("@/assets/images/gm-character-picker.webp");
  background-size: cover;
  background-position: center;
}

.picker__stack {
  position: relative;
  z-index: 1;
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap-sm);
  min-height: 0;
}

.picker__head {
  flex: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--gm-gap-sm);
}

.picker__title {
  margin: 0;
  font-family: var(--gm-font-display);
  font-size: var(--gm-text-lg);
  letter-spacing: var(--gm-track-title);
  color: var(--gm-accent);
}

.picker__controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: var(--gm-gap-sm);
  width: 100%;
}

.picker__search {
  min-width: 14rem;
  flex: 0 1 22rem;
}

.picker__sort {
  flex: 0 0 auto;
  width: 14rem;
}

.picker__segments {
  display: flex;
  gap: var(--gm-gap-xs);
}

.picker__count {
  font-family: var(--gm-font-mono);
  font-size: var(--gm-text-xs);
  color: var(--gm-text-dim);
}

.picker__body {
  position: relative;
  z-index: 1;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: var(--gm-gap-sm) var(--gm-gap-xs);
  border-top: 1px solid var(--gm-border-dim);
}

.picker__grid-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(4.5rem, 1fr));
  align-content: start;
  gap: var(--gm-gap);
}

.picker__none {
  height: 100%;
}

/* A pictogram, not a card: portrait first, the name only as a caption under it
   and in the tooltip, so a full roster reads as one glanceable board. */
.pc {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--gm-gap-xs);
  padding: var(--gm-gap-xs);
  background: none;
  border: none;
  color: var(--gm-text-muted);
  font-family: inherit;
  cursor: pointer;
}

.pc__avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  aspect-ratio: 1;
  border-radius: var(--gm-radius);
  border: 1px solid var(--gm-border-dim);
  background-color: var(--gm-surface-2);
  overflow: hidden;
  box-shadow: var(--gm-shadow-inset);
  transition: border-color var(--gm-duration) var(--gm-ease),
  box-shadow var(--gm-duration) var(--gm-ease),
  transform var(--gm-duration) var(--gm-ease);
}

.pc:hover .pc__avatar {
  border-color: var(--gm-border-hi);
  box-shadow: var(--gm-glow-accent);
  transform: translateY(-2px);
}

.pc:hover .pc__caption {
  color: var(--gm-accent);
}

.pc--selected .pc__avatar {
  border-color: var(--gm-accent);
  box-shadow: var(--gm-glow-ring);
}

.pc--selected .pc__caption {
  color: var(--gm-accent);
}

.pc--down .pc__avatar {
  filter: grayscale(1);
  border-color: var(--gm-danger);
  opacity: 0.6;
}

.pc__portrait {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
}

.pc__initials {
  font-family: var(--gm-font-display);
  font-size: var(--gm-text-base);
  letter-spacing: var(--gm-track-tight);
  color: var(--gm-text-dim);
}

.pc__caption {
  max-width: 100%;
  font-size: var(--gm-text-micro);
  letter-spacing: var(--gm-track-tight);
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: color var(--gm-duration) var(--gm-ease);
}

.pc__dot {
  position: absolute;
  top: 0.45rem;
  right: 0.45rem;
  width: 0.4rem;
  height: 0.4rem;
  border-radius: 50%;
  box-shadow: 0 0 0 2px var(--gm-surface-sunken);
}

.pc__dot--npc {
  background: var(--gm-info);
}

.pc__dot--down {
  top: auto;
  bottom: 1.35rem;
  background: var(--gm-danger);
}
</style>
